import React, { useState, useEffect } from "react";
import {
  message,
  Modal,
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { RiBaseStationFill } from "react-icons/ri";
import { IndoorPositioningStations } from "../../api";
import {
  canonicalDeviceId,
  deviceIdRule,
  findDeviceIdColumn,
  isValidDeviceId,
} from "../../utils/deviceId";

const { Option } = Select;

const BaseStationModal = ({ open, onClose, machineID }) => {
  const [baseStations, setBaseStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);

  const loadBaseStations = async () => {
    setLoading(true);
    try {
      const response = await IndoorPositioningStations.GetStations(machineID);
      if (response.code === 200) {
        setBaseStations(response.data || []);
      } else {
        message.error(response.message || "獲取基站資料失敗");
      }
    } catch (error) {
      console.error("載入基站資料失敗:", error);
      message.error("載入基站資料失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadBaseStations();
    }
  }, [open, machineID]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: `確定要刪除基站「${record.name}」嗎？`,
      okText: "確認",
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await IndoorPositioningStations.DeleteStation(
            record.id,
          );
          if (response.code === 200) {
            message.success("刪除基站成功");
            loadBaseStations();
          } else {
            message.error(response.message || "刪除基站失敗");
          }
        } catch (error) {
          console.error("刪除基站失敗:", error);
          message.error("刪除基站失敗");
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let response;

      if (editingRecord) {
        response = await IndoorPositioningStations.UpdateStation(
          editingRecord.id,
          {
            ...values,
            device_id: canonicalDeviceId(values.device_id),
            indoor_positioning_id: machineID,
          },
        );
      } else {
        response = await IndoorPositioningStations.CreateStation({
          ...values,
          device_id: canonicalDeviceId(values.device_id),
          indoor_positioning_id: machineID,
        });
      }

      if (response.code === 200) {
        message.success(editingRecord ? "更新基站成功" : "新增基站成功");
        setEditModalVisible(false);
        form.resetFields();
        setEditingRecord(null);
        loadBaseStations();
      } else {
        message.error(
          response.message || (editingRecord ? "更新基站失敗" : "新增基站失敗"),
        );
      }
    } catch (error) {
      console.error("提交基站失敗:", error);
      if (error.errorFields) {
        return;
      }
      message.error(editingRecord ? "更新基站失敗" : "新增基站失敗");
    }
  };

  const handleBatchImport = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        const headers = lines[0].split(",").map((h) => h.trim());

        const expectedHeaders = ["名稱", "類別"];
        const hasValidHeaders =
          expectedHeaders.every((h) => headers.includes(h)) &&
          findDeviceIdColumn(headers) === 1;

        if (!hasValidHeaders) {
          message.error(
            "CSV 格式錯誤：標題行應包含「名稱」、「裝置ID」、「類別」",
          );
          return false;
        }

        const importedStations = [];
        const skippedLines = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(",").map((v) => v.trim());
          if (values.length >= 3 && values[0] && values[1] && values[2]) {
            if (!isValidDeviceId(values[1])) {
              skippedLines.push(i + 1);
              continue;
            }
            const entry = {
              name: values[0],
              device_id: canonicalDeviceId(values[1]),
              category: values[2],
            };
            const txRaw = values[3]?.trim();
            if (txRaw) {
              const txNum = parseFloat(txRaw);
              if (!isNaN(txNum)) entry.tx_power = txNum;
            }
            importedStations.push(entry);
          }
        }

        if (skippedLines.length > 0) {
          message.warning(
            `已略過裝置 ID 格式不正確的第 ${skippedLines.join("、")} 行`,
          );
        }

        if (importedStations.length > 0) {
          const response = await IndoorPositioningStations.BatchImportStations(
            importedStations,
            machineID,
          );
          if (response.code === 200) {
            message.success(
              response.message ||
                `成功匯入 ${importedStations.length} 筆基站資料`,
            );
            setImportModalVisible(false);
            setFileList([]);
            loadBaseStations();
          } else {
            message.error(response.message || "匯入失敗");
          }
        } else {
          message.warning("沒有有效的資料可匯入");
        }
      } catch (error) {
        console.error("匯入失敗:", error);
        message.error("匯入失敗：" + error.message);
      }
    };
    reader.onerror = () => {
      message.error("讀取檔案失敗");
    };
    reader.readAsText(file);
    return false;
  };

  const handleDownloadExample = () => {
    const csvContent = `名稱,裝置ID,類別,發射功率(dBm)
基站1,AABBCCDDEEFF,定位基站,-65
基站2,112233445566,防護區,
基站3,AA11BB22CC33,其他基站,`;

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "基站匯入範例.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success("範例檔案下載成功");
  };

  const columns = [
    {
      title: (
        <span>
          <RiBaseStationFill style={{ marginRight: 4 }} />
          名稱
        </span>
      ),
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "裝置 ID",
      dataIndex: "device_id",
      key: "device_id",
      align: "center",
    },
    {
      title: "類別",
      dataIndex: "category",
      key: "category",
      align: "center",
      render: (category) => {
        const categoryMap = {
          定位基站: "定位基站",
          防護區: "防護區",
          其他基站: "其他基站",
        };
        return categoryMap[category] || category;
      },
    },
    {
      title: "發射功率",
      dataIndex: "tx_power",
      key: "tx_power",
      align: "center",
      render: (val) => (val != null ? `${val} dBm` : "預設"),
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編輯
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  const uploadProps = {
    beforeUpload: (file) => {
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      if (!isCSV) {
        message.error("只能上傳 CSV 檔案");
        return false;
      }
      handleBatchImport(file);
      return false;
    },
    fileList,
    accept: ".csv",
  };

  return (
    <>
      <Modal
        title={
          <span>
            <RiBaseStationFill style={{ marginRight: 8, fontSize: 18 }} />
            基站資料
          </span>
        }
        open={open}
        onCancel={onClose}
        footer={null}
        width={900}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span className="font-weight-bold mr-2">基站數量：</span>
              <span>{baseStations.length} 站</span>
            </div>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增基站
              </Button>
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportModalVisible(true)}
              >
                批次匯入基站
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={baseStations}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showTotal: (total) => `共 ${total} 筆資料`,
            }}
            locale={{
              emptyText: "暫無資料",
            }}
          />
        </div>
      </Modal>

      <Modal
        title={editingRecord ? "編輯基站" : "新增基站"}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
          setEditingRecord(null);
        }}
        onOk={handleSubmit}
        okText="確認"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="基站名稱"
            name="name"
            rules={[{ required: true, message: "請輸入基站名稱" }]}
          >
            <Input placeholder="請輸入基站名稱" />
          </Form.Item>
          <Form.Item
            label="基站裝置 ID"
            name="device_id"
            rules={[
              { required: true, message: "請輸入基站裝置 ID" },
              deviceIdRule,
            ]}
          >
            <Input placeholder="請輸入基站裝置 ID (例如: AABBCCDDEEFF)" />
          </Form.Item>
          <Form.Item
            label="基站類別"
            name="category"
            rules={[{ required: true, message: "請選擇類別" }]}
          >
            <Select placeholder="請選擇基站類別">
              <Option value="定位基站">定位基站</Option>
              <Option value="防護區">防護區</Option>
              <Option value="其他基站">其他基站</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="發射功率 (dBm)"
            name="tx_power"
            tooltip="校準後的1公尺發射功率。留空表示使用系統預設值 (-59 dBm)。僅對「定位基站」有效。"
            rules={[
              {
                validator: (_, value) => {
                  if (value == null || value === "") return Promise.resolve();
                  const n = Number(value);
                  if (isNaN(n) || n < -120 || n >= 0)
                    return Promise.reject(
                      "發射功率需介於 -120 至 0 dBm (不含 0)",
                    );
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder="留空使用預設值 (-59 dBm)"
              min={-120}
              max={-0.1}
              step={0.5}
              style={{ width: "100%" }}
              addonAfter="dBm"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批次匯入基站"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setFileList([]);
        }}
        footer={null}
      >
        <div className="p-3">
          <p className="mb-3">請上傳基站 CSV 檔案，格式如下：</p>
          <div
            className="mb-3"
            style={{
              backgroundColor: "#f5f5f5",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <code>
              名稱,裝置ID,類別,發射功率(dBm)
              <br />
              基站1,AABBCCDDEEFF,定位基站,-65
              <br />
              基站2,112233445566,防護區,
            </code>
          </div>
          <Space>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>選擇 CSV 檔案</Button>
            </Upload>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadExample}>
              下載範例檔案
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default BaseStationModal;
